import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Check, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DEFAULT_SIZE, labelLines, labelsPdf, qrSvg } from "@/lib/labels";
import { api } from "@/lib/api";
import { BrotherGuide } from "@/components/BrotherGuide";
import { LabelSizePicker } from "@/components/LabelSizePicker";

const SIZE_KEY = "cellar_label_size_v2";
const savedSize = () => {
  try {
    const s = JSON.parse(localStorage.getItem(SIZE_KEY));
    return s.w >= 20 && s.w <= 120 && s.h >= 10 && s.h <= 100 ? { w: s.w, h: s.h } : DEFAULT_SIZE;
  } catch {
    return DEFAULT_SIZE;
  }
};

const Label = ({ l, size, testid }) => {
  const { L, lines } = labelLines(l, size);
  const qr = qrSvg(l, L.q);
  const off = (L.q - qr.mm) / 2;
  return (
    <div className="label-face" style={{ width: `${size.w}mm`, height: `${size.h}mm` }} data-testid={testid}>
      <div className="label-qr" style={{ left: `${L.qx + off}mm`, top: `${L.qy + off}mm`, width: `${qr.mm}mm`, height: `${qr.mm}mm` }} dangerouslySetInnerHTML={{ __html: qr.svg }} />
      {lines.map((x) => (
        <p key={x.key} className="label-line" data-testid={testid && `${testid}-${x.key}`}
          style={{ fontSize: `${x.pt}pt`, fontWeight: x.bold ? 700 : 400, top: `${x.y - 0.8 * x.pt * 0.3528}mm`, color: x.grey ? "#555" : "#000",
            ...(L.tall ? { left: `${L.pad}mm`, width: `${L.maxW}mm`, textAlign: "center" } : { left: `${L.x}mm` }) }}>
          {x.text}
        </p>
      ))}
    </div>
  );
};

const PrintSheet = ({ labels, size }) => createPortal(
  <div className="label-print-sheet" data-testid="label-print-sheet">
    <style>{`@page { size: ${size.w}mm ${size.h}mm; margin: 0; }`}</style>
    {labels.map((l) => <Label key={l.barcode} l={l} size={size} />)}
  </div>,
  document.body,
);

export default function PrintLabels() {
  const navigate = useNavigate();
  const [labels, setLabels] = useState(() => JSON.parse(sessionStorage.getItem("cellar_labels") || "[]"));
  const [sel, setSel] = useState(() => labels.map((l) => l.barcode));
  const [busy, setBusy] = useState(false);
  const [size, setSize] = useState(savedSize);
  const changeSize = (s) => {
    setSize(s);
    localStorage.setItem(SIZE_KEY, JSON.stringify(s));
  };
  useEffect(() => {
    document.body.classList.add("printing-labels");
    Promise.all(labels.map((l) => api.get(`/scan/${l.barcode}`).then((r) => (l.kind === "box" ? { ...l, wines: r.data.wines, bottle_count: r.data.bottle_count } : { ...l, serial: r.data.serial_number || "" })).catch(() => l))).then(setLabels);
    return () => {
      document.body.classList.remove("printing-labels");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dims = `${size.w} × ${size.h} mm`;
  const toggle = (c) => setSel((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
  const chosen = labels.filter((l) => sel.includes(l.barcode));

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await labelsPdf(chosen, size));
      a.download = `cellar-labels-${size.w}x${size.h}mm.pdf`;
      a.click();
    } catch {
      toast.error("Could not create the labels");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0A0B]">
      <div className="max-w-5xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#A89F91] hover:text-[#D4AF37] mb-3" data-testid="print-back-button"><ArrowLeft className="w-4 h-4" /> Back</button>
          <h2 className="font-serif text-4xl text-[#F5F0EB]">Labels · <span data-testid="label-size-title">{dims}</span></h2>
          <p className="text-sm text-[#A89F91] mt-1 max-w-xl" data-testid="label-count">
            {chosen.length} of {labels.length} label{labels.length === 1 ? "" : "s"} selected. Click a label to leave it out. Print sends one {dims} label per page straight to the printer.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setSel(sel.length === labels.length ? [] : labels.map((l) => l.barcode))} disabled={!labels.length} data-testid="toggle-all-labels-button">
            {sel.length === labels.length ? "Select none" : "Select all"}
          </Button>
          <Button variant="outline" onClick={downloadPdf} disabled={!chosen.length || busy} className="h-12" data-testid="download-labels-pdf">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button onClick={() => window.print()} disabled={!chosen.length} className="h-12 px-6 bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="trigger-print-button">
            <Printer className="w-4 h-4 mr-2" /> Print {chosen.length}
          </Button>
        </div>
      </div>
      <LabelSizePicker size={size} onChange={changeSize} />
      <BrotherGuide size={size} />
      <p className="max-w-5xl mx-auto px-6 mb-3 text-xs uppercase tracking-[0.2em] text-[#6E665D]">Preview · real size</p>
      <div className="max-w-5xl mx-auto px-6 pb-16 flex flex-wrap gap-6">
        {labels.map((l) => {
          const on = sel.includes(l.barcode);
          return (
            <div key={l.barcode} onClick={() => toggle(l.barcode)} role="button" aria-pressed={on} title="Click to include or leave out"
              className={`relative cursor-pointer shadow-lg outline outline-2 outline-offset-4 transition-[outline-color,opacity] ${on ? "outline-[#D4AF37]" : "outline-transparent opacity-35"}`}
              data-testid={`sticker-label-${l.barcode}`}>
              {on && <span className="absolute -top-2 -right-2 z-10 w-4 h-4 rounded-full bg-[#D4AF37] text-[#0D0A0B] flex items-center justify-center"><Check style={{ width: 10, height: 10 }} /></span>}
              <Label l={l} size={size} testid={`label-face-${l.barcode}`} />
            </div>
          );
        })}
        {!labels.length && <p className="text-[#6E665D]">No labels selected. Go back and pick bottles or boxes.</p>}
      </div>
      <PrintSheet labels={chosen} size={size} />
    </div>
  );
}
